



















































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { DetailGraphLoadingIndicator } from '@/components/graph/LoadingIndicatorConfig';

import constants        from '@/constants';

import LineGraph3        from '@/components/graph/LineGraph3.vue';

import DetailGraphLegendToggleDesktop       from '@/components/detail/DetailGraphLegendToggleDesktop.vue';
import DetailGraphLegendToggleMobile from '@/components/detail/DetailGraphLegendToggleMobile.vue';
import DetailGraphTemporalSelector from '@/components/detail/DetailGraphTemporalSelector.vue';

import OrderedPair from '@/models/OrderedPair';
import { overlayHistoricalDataOntoTimeSeries, overlayTimeSeries } from '@/common/functions';

const DEFAULT_TIME_PERIOD = constants.TimePeriods.get('OneWeek');
const TOOLTIP_LINE_CIRCLE_SIZE = 12;

@Component({
  name: 'SiteGraph',
  components: {
    LineGraph3,
    DetailGraphLegendToggleMobile,
    DetailGraphLegendToggleDesktop,
    DetailGraphTemporalSelector,
  }
})
export default class SiteGraph extends Vue {

  @Prop({ required: true }) siteNumber: string | undefined;

  selectedTimePeriod = DEFAULT_TIME_PERIOD;
  selectedTimePeriodKey = DEFAULT_TIME_PERIOD;

  activeFlowVariableSelection = constants.FlowVariables.Discharge;

  loadingIndicatorConfig = DetailGraphLoadingIndicator;

  xAxisConfig = {

    // TODO GRAPH THEMES: Move to theme def
    showAxis : true,
    showAxisTicks : true,
    showAxisTickLabels : true,
    axisPointer: {
      label: {
        show: false,
        formatter: (config: Record<string, any>) => {
          const date = new Date(config.value)
          return date.toLocaleTimeString() + '\n' + date.toLocaleDateString();
        }
      }
    }
  };

  // [done] 1. TODO HISTORICAL
  //
  // REMOVE, DEPRECATED
  // historicalDischargeTimeSeriesOverlay: Array<OrderedPair> = [];

  /**
   * Time series for the 'flow' variable displayed on the graph
   * - Could be Discharge or Gauge Height
   * - Discharge is default
   * - User can change it
   * - Common for only one or the other to exist
   *
   * TODO: great opportunity to experiment with the composition API
   *
   * v2 plugin https://github.com/vuejs/composition-api
   * v3 docs https://v3.vuejs.org/guide/composition-api-introduction.html
   */
  historicalFlowTimeSeriesOverlay: Array<OrderedPair> = [];

  showValuesFromTooltip = false;

  flowFromTooltip = '';
  historicalFlowFromTooltip = '';
  waterTempFromTooltip = '';

  currentFlow = '';
  currentHistoricalFlow = '';
  currentWaterTemp = '';

  // Whether or not it's ok to render the toggle (must happen after the charts config is set)
  okToRenderToggle = false;

  // Whether or not it's ok to render the toggle (must happen after the toggle is rendered)
  okToRenderChart = false;

  // Config for the chart. This used to be (and was much better as) a `get` property, but had to change
  // it because the toggle relied on side effects that happened as part of its initialization, leading
  // to a situation where the chart would always render before historical data would render in the toggle,
  // fucking up the layout
  echartsTimeSeriesConfig = {};

  // Whether the component should wait until the site's XHRs come back before attempting to render stuff
  waitForSiteToLoad = false;

  resizeObserver!: ResizeObserver;
  intersectionObserver!: IntersectionObserver;

  //
  // Lifecycle methods
  //

  mounted() {

    // If we're still loading the site... set a flag to ensure we render the toggle and chart when it's time
    //
    // See watcher below on `isLoadingSite`
    if (this.isLoadingSite) {
      this.waitForSiteToLoad = true;
    }
    // Otherwise, go ahead and render stuff now
    else {
      this.setChartConfigThenRenderToggleThenRenderChart();
    }

    // FIXME: Remove this attempt to fix the graph resize bug... kinda works kinda doesn't?
    this.intersectionObserver =  new IntersectionObserver(() =>
      {
        // console.log('running intersection observer')
        this.$nextTick(() => {
          this.resize();
        })
      },
      {
        root: this.$el
      }
    );
    this.intersectionObserver.observe((this.$refs.toggleWrapper as HTMLElement));
  }

  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  resize() {
    if (this.$refs.lineGraph) {
      (this.$refs.lineGraph as any).resize();
    }
  }

  @Watch('isLoadingSite')
  onIsLoadingSiteChanged(isLoadingSite: boolean, oldIsLoadingSite: boolean) {
    // If we were waiting for the site to load, and it's now loaded, render stuff
    if (this.waitForSiteToLoad && !isLoadingSite) {
      this.setChartConfigThenRenderToggleThenRenderChart();
    }
  }

  /**
   * A terrible name for a terrible method
   *
   * Refactor this component please!
   */
  setChartConfigThenRenderToggleThenRenderChart() {
    this.setEchartsTimeSeriesConfig();
    this.okToRenderToggle = true;
    this.$nextTick(() => {
      this.$nextTick(() =>{
        this.okToRenderChart = true;
      });
    })
  }

  //
  // Computed properties
  //

  get isLoadingSite() {
    return this.$store.state.uiState.isLoadingSite;
  }

  get activeSiteData() {
    return this.$store.state.activeSite.data;
  }

  get activeSiteHasDischarge() {
    return !!this.activeSiteData.discharge.rawInstantTimeSeries;
  }

  get isDesktop() {
    return this.$store.state.uiState.isDesktop;
  }

  get flowVariableOptions() {

    // FIXME: this is false!!!
    // - some sites have no flow variable
    // - some sites might not have discharge?
    //
    // All sites (we think) have Gauge Height
    const options = [
      constants.FlowVariables.GaugeHeight
    ];

    // Add Discharge if it's a thing
    if (this.activeSiteHasDischarge) {
      options.push(constants.FlowVariables.Discharge);
    }

    // Order matters, and discharge comes before gauge height
    options.reverse();
    return options;
  }

  get activeFlowVariable() {
    if (this.activeSiteHasDischarge && this.activeFlowVariableSelection === constants.FlowVariables.Discharge) {
      return constants.FlowVariables.Discharge;
    }
    return constants.FlowVariables.GaugeHeight;
  }

  /**
   * Whether or not the current dataset uses one value per day
   */
  get isUsingDailyValues() {

    const result =      this.selectedTimePeriodKey === constants.TimePeriods.get("ThreeMonths").value
                    ||  this.selectedTimePeriodKey === constants.TimePeriods.get("SixMonths").value
                    ||  this.selectedTimePeriodKey === constants.TimePeriods.get("OneYear").value

    return result;
  }

  /**
   * Get "the right" kind of flow variable...
   *
   * - Discharge, by default
   * - Unless the user chose Gauge Height
   * - Or, unless Gauge Height is the only variable...
   */
  get baseFlowTimeSeries() {
    if (this.activeSiteHasDischarge && this.activeFlowVariable === constants.FlowVariables.Discharge) {
      return this.baseDischargeTimeSeries;
    }
    return this.baseGaugeHeightTimeSeries;
  }

  get baseDischargeTimeSeries() {
    const discharge = this.activeSiteData.discharge;
    let result = discharge.lastWeekTimeSeries;
    switch(this.selectedTimePeriodKey) {

      // Worst constant definition ever...
      case constants.TimePeriods.get("OneDay").value:
        result = discharge.lastDayTimeSeries;
        break;
      case constants.TimePeriods.get("OneWeek").value:
        result = discharge.lastWeekTimeSeries;
        break;
      case constants.TimePeriods.get("OneMonth").value:
        result = discharge.lastMonthTimeSeries;
        break;
      case constants.TimePeriods.get("ThreeMonths").value:
        result = discharge.last3MonthsTimeSeries;
        break;
      case constants.TimePeriods.get("SixMonths").value:
        result = discharge.last6MonthsTimeSeries;
        break;
      case constants.TimePeriods.get("OneYear").value:
        result = discharge.lastYearTimeSeries;
        break;
    }

    return result;
  }

  get baseGaugeHeightTimeSeries() {
    const gaugeHeight = this.activeSiteData.gaugeHeight;
    let result = gaugeHeight.lastWeekTimeSeries;
    switch(this.selectedTimePeriodKey) {

      // Worst constant definition ever...
      case constants.TimePeriods.get("OneDay").value:
        result = gaugeHeight.lastDayTimeSeries;
        break;
      case constants.TimePeriods.get("OneWeek").value:
        result = gaugeHeight.lastWeekTimeSeries;
        break;
      case constants.TimePeriods.get("OneMonth").value:
        result = gaugeHeight.lastMonthTimeSeries;
        break;
      case constants.TimePeriods.get("ThreeMonths").value:
        result = gaugeHeight.last3MonthsTimeSeries;
        break;
      case constants.TimePeriods.get("SixMonths").value:
        result = gaugeHeight.last6MonthsTimeSeries;
        break;
      case constants.TimePeriods.get("OneYear").value:
        result = gaugeHeight.lastYearTimeSeries;
        break;
    }

    return result;
  }

  get waterTempTimeSeries() {
    const waterTempDataSet = this.activeSiteData.waterTemp;
    const hasWaterTemp = !!waterTempDataSet.rawInstantTimeSeries

    if (hasWaterTemp) {
      let result = waterTempDataSet.lastWeekTimeSeries;
      switch(this.selectedTimePeriodKey) {

        // Worst constant definition ever...
        case constants.TimePeriods.get("OneDay").value:
          result = waterTempDataSet.lastDayTimeSeries;
          break;
        case constants.TimePeriods.get("OneWeek").value:
          result = waterTempDataSet.lastWeekTimeSeries;
          break;
        case constants.TimePeriods.get("OneMonth").value:
          result = waterTempDataSet.lastMonthTimeSeries;
          break;
        case constants.TimePeriods.get("ThreeMonths").value:
          result = waterTempDataSet.last3MonthsTimeSeries;
          break;
        case constants.TimePeriods.get("SixMonths").value:
          result = waterTempDataSet.last6MonthsTimeSeries;
          break;
        case constants.TimePeriods.get("OneYear").value:
          result = waterTempDataSet.lastYearTimeSeries;
          break;
      }
      return result;
    }
  }

  generateSnapshotDisplay(value: any, units: string) {
    // If we have an invalid (non-numeric string) value, don't show units
    if (isNaN(value) || value === '') {
      units = '';
    }
    return {
      value,
      units
    };
  }

  get flowSnapshot() {
    const value: any = this.showValuesFromTooltip ? this.flowFromTooltip : this.currentFlow;
    let units = this.activeFlowVariable.units;
    return this.generateSnapshotDisplay(value, units);
  }

  get historicalAverageSnapshot() {
    const value: any = this.showValuesFromTooltip ? this.historicalFlowFromTooltip : this.currentHistoricalFlow;
    let units = this.activeFlowVariable.units;
    return this.generateSnapshotDisplay(value, units);
  }

  get waterTempSnapshot() {
    const value: any = this.showValuesFromTooltip ? this.waterTempFromTooltip : this.currentWaterTemp;
    let units = constants.SecondaryVariables.WaterTemp.units;
    return this.generateSnapshotDisplay(value, units);
  }

  generateFlowTimeSeries(flowVariable: any, units: any, timeSeries: any) {

    let flowMax: Number = 0;
    timeSeries.forEach((dataPoint: OrderedPair) => {
      const asFugginNumber = new Number(dataPoint.y);
        if (asFugginNumber > flowMax) {
          flowMax = asFugginNumber;
        }
      }
    );

    // Add the flow time series (either discharge or gauge height)
    return {
      name        : units,
      id          : flowVariable.display,
      dataPoints  : timeSeries,

      // BELOW THIS - MOVE TO THEME FILE
      lineColor   : constants.COLORS.OSF_BLUE,
      symbol: 'circle',
      symbolSize: TOOLTIP_LINE_CIRCLE_SIZE,
      itemStyle: {
        color   : constants.COLORS.OSF_BLUE,
      },

      axisConfig  : {
        showAxis : false,
        showAxisTickLabels  : true,
        showAxisSplitLines  : true,
        name                : units,
        id                  : flowVariable.display,
        splitLineType       : 'solid',
        // axisPointer: {
        //   show: false,
        //   label: {
        //     show: false
        //   }
        // },
      },
      max: flowMax,
      isDischargeTimeSeries: flowVariable.key === constants.FlowVariables.Discharge.key
    };
  }

  generateAndSetHistoricalTimeSeriesOverlay(flowVariable: any, units: any, timeSeries: any) {
    // Add Historical data (Discharge only)

    // Reset historical flow overlay (in case it doesn't exist on *this* dataset)
    this.historicalFlowTimeSeriesOverlay = [];

    // Get historical data for the active flow variable
    const historicalDataMap = (flowVariable === constants.FlowVariables.Discharge) ?
        this.activeSiteData.discharge.historicalDataMap : this.activeSiteData.gaugeHeight.historicalDataMap;

    if (!historicalDataMap) {
      return null;
    }

    // Get the subset of the historical time series that can be overlayed onto the graph
    if (historicalDataMap) {
      this.historicalFlowTimeSeriesOverlay = overlayHistoricalDataOntoTimeSeries({ data: timeSeries} , historicalDataMap );
    }

    // Find the maximum value in this time series; used for scaling the map
    //
    // FIXME: only do this when historical data exists
    let historicalMax: Number = 0;
    this.historicalFlowTimeSeriesOverlay.forEach((dataPoint: OrderedPair) => {
      const historicalValue = new Number(dataPoint.y);
        if (historicalValue > historicalMax) {
          historicalMax = historicalValue;
        }
      }
    );

    return {
      formatter: function(dataPoint: OrderedPair) {
        return [dataPoint.x, dataPoint.y];
      },

      name      : units,
      id        : `${flowVariable.display} (Historical)`,
      dataPoints: this.historicalFlowTimeSeriesOverlay,

      // BELOW THIS - MOVE TO THEME FILE
      type      : 'line',
      symbol: 'circle',
      symbolSize: TOOLTIP_LINE_CIRCLE_SIZE,
      lineStyleType: 'dashed',
      lineColor: constants.COLORS.OSF_DARK_GRAY,
      itemStyle: {
        color: constants.COLORS.OSF_DARK_GRAY,
      },

      axisConfig  : {
        // axisPointer: {
        //   show: false,
        //   label: {
        //     show: false
        //   }
        // }
      },

      max: historicalMax,
      isDischargeTimeSeries: true
    }
  }

  generateWaterTempTimeSeries(flowVariable: any, units: any, timeSeries: any) {
    const waterTempDef = constants.SecondaryVariables.WaterTemp;

    let waterTempMax: Number = 0;
    // const timeSeries = this.waterTempTimeSeries.data;
    timeSeries.forEach((dataPoint: OrderedPair) => {
      const asFugginNumber = new Number(dataPoint.y);
        if (asFugginNumber > waterTempMax) {
          waterTempMax = asFugginNumber;
        }
      }
    );

    return {
      name        : waterTempDef.units,
      id          : waterTempDef.display,
      dataPoints  : this.waterTempTimeSeries.data,

      // MOVE TO BUILDER
      formatter: (dataPoint: OrderedPair) => {
        return [dataPoint.x, dataPoint.y];
      },

      // BELOW THIS - MOVE TO THEME FILE
      lineColor   : constants.COLORS.GREEN,
      symbol: 'circle',
      symbolSize: TOOLTIP_LINE_CIRCLE_SIZE,
      itemStyle: {
        color   : constants.COLORS.GREEN,
      },

      axisConfig  : {
        showAxis : false,
        showAxisTickLabels  : true,
        showAxisSplitLines  : true,
        name                : waterTempDef.units,
        id                  : waterTempDef.display,
        splitLineType       : 'dotted',
        // axisPointer: {
        //   show: false,
        //   label: {
        //     show: false
        //   }
        // },
        max: waterTempMax
      }
    };
  }

  /**
   * Get the `series` parameter that eventually gets passed into the Echarts instance
   *
   * It's an array of config objects, one for each data set on the graph
   *
   * REFACTOR: This method is a giant piece of shit
   */
  setEchartsTimeSeriesConfig() {

    console.log('time series config running...')

    const flowVariable = this.activeFlowVariable;
    const units = flowVariable.units;
    const timeSeries = this.baseFlowTimeSeries && this.baseFlowTimeSeries.data || [];

    // Add the flow time series (either discharge or gauge height)
    const flowTimeSeries = this.generateFlowTimeSeries(flowVariable, units, timeSeries);

    const echartsTimeSeriesConfig = [];
    echartsTimeSeriesConfig.push(flowTimeSeries);

    const historicalTimeSeries = this.generateAndSetHistoricalTimeSeriesOverlay(flowVariable, units, timeSeries);
    if (historicalTimeSeries) {
      echartsTimeSeriesConfig.push(historicalTimeSeries);
    }

    if (this.waterTempTimeSeries) {
      const waterTempTimeSeries = this.generateWaterTempTimeSeries(flowVariable, units, this.waterTempTimeSeries.data);
      echartsTimeSeriesConfig.push(waterTempTimeSeries);
    }

    this.echartsTimeSeriesConfig = echartsTimeSeriesConfig;
  }

  //
  // Event Handlers
  //

  handleFlowVariableChanged(flowVariableKey: string) {
    const discharge = constants.FlowVariables.Discharge;
    const gaugeHeight = constants.FlowVariables.GaugeHeight;

    this.activeFlowVariableSelection = flowVariableKey === discharge.key ? discharge : gaugeHeight;
    this.currentHistoricalFlow = '';
    this.historicalFlowFromTooltip = '';

    this.okToRenderChart = false;
    this.$nextTick(() => {
      this.setChartConfigThenRenderToggleThenRenderChart();
    })
  }

  handleTimePeriodChanged(timePeriod: Record<string, any>) {
    this.selectedTimePeriod = timePeriod;
    this.selectedTimePeriodKey = timePeriod.value;
    this.$nextTick(() => {
      this.setChartConfigThenRenderToggleThenRenderChart();
    })
  }

  handleTooltipUpdated(updates: Array<any>) {
    this.showValuesFromTooltip = true;

    updates.forEach(update => {
      switch(update.seriesId) {
        case 'Discharge':
        case 'Gauge Height':
          this.flowFromTooltip = update.value
          break;
        case 'Water Temperature':
          this.waterTempFromTooltip = update.value
          break;
        case 'Discharge (Historical)':
          this.historicalFlowFromTooltip = update.value
          break;
        case 'Gauge Height (Historical)':
          this.historicalFlowFromTooltip = update.value
          break;
      }
    })
  }

  handleSetCurrentTooltipValue(update: Record<string, string>) {
    switch(update.seriesId) {
      case 'Discharge':
      case 'Gauge Height':
        this.currentFlow = update.value
        break;
      case 'Water Temperature':
        this.currentWaterTemp = update.value
        break;
      case 'Discharge (Historical)':
        this.currentHistoricalFlow = update.value
        break;
      case 'Gauge Height (Historical)':
        this.currentHistoricalFlow = update.value
        break;
    }
  }

}

